import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Ellipsis,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import type { TranslateFn } from "../types";

type CatalogPaginationProps = {
  t: TranslateFn;
  isRTL: boolean;
  loading: boolean;
  page: number;
  pageCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

type PageItem = number | "start-ellipsis" | "end-ellipsis";

const getPageItems = (page: number, pageCount: number): PageItem[] => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  const items: PageItem[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);

  if (start > 2) items.push("start-ellipsis");
  for (let value = start; value <= end; value += 1) items.push(value);
  if (end < pageCount - 1) items.push("end-ellipsis");
  items.push(pageCount);

  return items;
};

const CatalogPagination = ({
  t,
  isRTL,
  loading,
  page,
  pageCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: CatalogPaginationProps) => {
  const PreviousIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;
  const FirstIcon = isRTL ? ChevronsRight : ChevronsLeft;
  const LastIcon = isRTL ? ChevronsLeft : ChevronsRight;

  return (
    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        <span className="text-xs font-normal text-muted-foreground">
          {t("common.rowsPerPage")}
        </span>
        <Select
          dir={isRTL ? "rtl" : "ltr"}
          disabled={loading}
          value={String(pageSize)}
          onValueChange={(value) => onPageSizeChange(Number(value))}
        >
          <SelectTrigger
            data-testid="ai-catalog-page-size-select"
            className="h-9 w-20 bg-input shadow-xs"
            aria-label={t("common.rowsPerPage")}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[10, 25, 50].map((size) => (
              <SelectItem
                key={size}
                data-testid={`ai-catalog-page-size-option-${size}`}
                value={String(size)}
              >
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <nav
        data-testid="ai-catalog-pagination"
        aria-label="Pagination"
        className="flex items-center gap-1 self-end sm:self-auto"
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="ai-catalog-pagination-first"
          aria-label="Go to first page"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(1)}
        >
          <FirstIcon aria-hidden="true" className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="h-9 px-2 sm:px-3"
          data-testid="ai-catalog-pagination-previous"
          aria-label={t("common.previous")}
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <PreviousIcon aria-hidden="true" className="size-4" />
          <span className="hidden sm:inline">{t("common.previous")}</span>
        </Button>

        {getPageItems(page, pageCount).map((item) =>
          typeof item === "number" ? (
            <Button
              type="button"
              key={item}
              variant={item === page ? "outline" : "ghost"}
              size="icon"
              data-testid={`ai-catalog-pagination-page-${item}`}
              aria-label={`Page ${item}`}
              aria-current={item === page ? "page" : undefined}
              disabled={loading}
              className={cn("size-9", item === page && "bg-accent")}
              onClick={() => onPageChange(item)}
            >
              {item}
            </Button>
          ) : (
            <span
              key={item}
              data-testid={`ai-catalog-pagination-${item}`}
              className="flex size-9 items-center justify-center text-muted-foreground"
            >
              <Ellipsis aria-hidden="true" className="size-4" />
              <span className="sr-only">More pages</span>
            </span>
          ),
        )}

        <Button
          type="button"
          variant="ghost"
          className="h-9 px-2 sm:px-3"
          data-testid="ai-catalog-pagination-next"
          aria-label={t("common.next")}
          disabled={loading || page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          <span className="hidden sm:inline">{t("common.next")}</span>
          <NextIcon aria-hidden="true" className="size-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="ai-catalog-pagination-last"
          aria-label="Go to last page"
          disabled={loading || page >= pageCount}
          onClick={() => onPageChange(pageCount)}
        >
          <LastIcon aria-hidden="true" className="size-4" />
        </Button>
      </nav>
    </div>
  );
};

export default CatalogPagination;
